import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Checkbox,
  Grid,
  Card,
  CardContent,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  TablePagination
} from '@mui/material';
import {
  Send as SendIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Receipt as ReceiptIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import axios from 'axios';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';

interface Invoice {
  _id: string;
  invoiceNumber: string;
  date: string;
  customerName: string;
  total: number;
  certificateName?: string;
  zatcaPhase2: {
    submissionStatus: string;
    submissionDate?: string;
    uuid?: string;
  };
  creditNotes: CreditNote[];
  hasCreditNote: boolean;
  hasSystemReturn?: boolean; // استرجاع من النظام الفعلي
  systemReturnCount?: number; // عدد الاسترجاعات من النظام الفعلي
  isB2B?: boolean; // نوع الفاتورة
  submissionAction?: string; // reporting أو clearance
}

interface CreditNote {
  _id: string;
  creditNoteNumber: string;
  creditNoteDate: string;
  creditNoteAmount: number;
  submissionStatus: string;
  submissionDate?: string;
  certificateName?: string;
  responseCode?: string;
  validationErrors?: Array<{
    errorType: string;
    field?: string;
    code?: string;
    message: string;
    severity: 'error' | 'warning' | 'info';
  }>;
}

interface Statistics {
  totalInvoices: number;
  totalInvoicesAmount: number;
  creditNotes: {
    total: number;
    pending: number;
    submitted: number;
    accepted: number;
    rejected: number;
    warning: number;
    failed: number;
    totalAmount: number;
    pendingAmount: number;
    submittedAmount: number;
    acceptedAmount: number;
    rejectedAmount: number;
    warningAmount: number;
    failedAmount: number;
  };
  systemReturns: {
    count: number;
    totalAmount: number;
  };
}

interface Certificate {
  _id: string;
  name: string;
  environment: string;
  createdAt: string;
}

const ZatcaInvoiceReportPage: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    certificateId: '',
    status: '',
    search: ''
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [detailsDialog, setDetailsDialog] = useState<{
    open: boolean;
    creditNote: CreditNote | null;
  }>({ open: false, creditNote: null });

  // جلب البيانات عند تحميل الصفحة
  useEffect(() => {
    fetchData();
    fetchCertificates();
  }, [page, filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [invoicesRes, statsRes] = await Promise.all([
        axios.get('/api/zatca-invoice-report', {
          params: { page: page + 1, limit: rowsPerPage, ...filters }
        }),
        axios.get('/api/zatca-invoice-report/statistics', {
          params: filters
        })
      ]);

      setInvoices(invoicesRes.data.data);
      setTotalCount(invoicesRes.data.pagination.total);
      setStatistics(statsRes.data.data);
    } catch (error) {
      console.error('خطأ في جلب البيانات:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCertificates = async () => {
    try {
      const response = await axios.get('/api/zatca-invoice-report/certificates');
      setCertificates(response.data.data);
    } catch (error) {
      console.error('خطأ في جلب الشهادات:', error);
    }
  };

  // إنشاء وإرسال فاتورة استرجاع واحدة
  const handleCreateCreditNote = async (invoiceId: string) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `/api/zatca-invoice-report/credit-note/${invoiceId}`
      );
      
      if (response.data.success) {
        fetchData();
      }
    } catch (error: any) {
      console.error('خطأ في إنشاء فاتورة الاسترجاع:', error);
    } finally {
      setLoading(false);
    }
  };

  // إنشاء وإرسال فواتير استرجاع متعددة
  const handleBulkCreateCreditNotes = async () => {
    if (selectedInvoices.length === 0) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        '/api/zatca-invoice-report/credit-notes/bulk',
        { invoiceIds: selectedInvoices }
      );
      
      if (response.data.success) {
        setSelectedInvoices([]);
        fetchData();
      }
    } catch (error: any) {
      console.error('خطأ في معالجة الفواتير:', error);
    } finally {
      setLoading(false);
    }
  };

  // تحديد/إلغاء تحديد فاتورة
  const handleSelectInvoice = (invoiceId: string) => {
    setSelectedInvoices(prev =>
      prev.includes(invoiceId)
        ? prev.filter(id => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };

  // تحديد/إلغاء تحديد الكل
  const handleSelectAll = () => {
    if (selectedInvoices.length === invoices.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(invoices.map(inv => inv._id));
    }
  };
  // دوال التعامل مع Pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  // عرض تفاصيل فاتورة الاسترجاع
  const handleViewDetails = async (creditNoteId: string) => {
    try {
      const response = await axios.get(
        `/api/zatca-invoice-report/credit-note/${creditNoteId}`
      );
      console.log('📋 بيانات فاتورة الاسترجاع الكاملة:', response.data.data);
      console.log('🔍 validationErrors:', response.data.data.validationErrors);
      console.log('📊 نوع validationErrors:', typeof response.data.data.validationErrors);
      console.log('📏 طول validationErrors:', response.data.data.validationErrors?.length);
      setDetailsDialog({ open: true, creditNote: response.data.data });
    } catch (error) {
      console.error('خطأ في جلب التفاصيل:', error);
    }
  };

  // الحصول على لون حالة الإرسال
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted':
      case 'submitted':
        return 'success';
      case 'rejected':
      case 'failed':
        return 'error';
      case 'warning':
        return 'warning';
      case 'pending':
        return 'info';
      default:
        return 'default';
    }
  };

  // الحصول على نص حالة الإرسال
  const getStatusText = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'مقبول';
      case 'submitted':
        return 'مرسل';
      case 'rejected':
        return 'مرفوض';
      case 'failed':
        return 'فشل';
      case 'warning':
        return 'تحذير';
      case 'pending':
        return 'قيد الانتظار';
      default:
        return status;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* العنوان */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          فواتير إرسال للهيئة
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={fetchData}
          disabled={loading}
        >
          تحديث
        </Button>
      </Box>

      {/* الإحصائيات */}
      {statistics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  إجمالي الفواتير
                </Typography>
                <Typography variant="h4">{statistics.totalInvoices}</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  {(statistics.totalInvoicesAmount || 0).toFixed(2)} ر.س
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          {/* مربع إحصائي جديد: إجمالي فواتير الاسترجاع */}
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ bgcolor: 'info.lighter', border: '2px solid', borderColor: 'info.main' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom fontWeight="bold">
                  إجمالي فواتير الاسترجاع
                </Typography>
                <Typography variant="h4">
                  {statistics.systemReturns.count + statistics.creditNotes.total}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  {(statistics.systemReturns.totalAmount + statistics.creditNotes.totalAmount).toFixed(2)} ر.س
                </Typography>
                <Typography variant="body2" color="primary" sx={{ mt: 0.5, display: 'block', fontWeight: 'bold' }}>
                  {statistics.totalInvoicesAmount > 0
                    ? `${(((statistics.systemReturns.totalAmount + statistics.creditNotes.totalAmount) / statistics.totalInvoicesAmount) * 100).toFixed(1)}%`
                    : '0%'
                  } من الفواتير الأصلية
                </Typography>
                <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" display="block" color="textSecondary">
                    نظام: {statistics.systemReturns.count} ({statistics.systemReturns.totalAmount.toFixed(2)} ر.س)
                  </Typography>
                  <Typography variant="body2" display="block" color="textSecondary">
                    هيئة: {statistics.creditNotes.total} ({statistics.creditNotes.totalAmount.toFixed(2)} ر.س)
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ bgcolor: 'warning.lighter' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  فواتير مسترجعة (نظام)
                </Typography>
                <Typography variant="h4">{statistics.systemReturns.count}</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  {statistics.systemReturns.totalAmount.toFixed(2)} ر.س
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  فواتير الاسترجاع (هيئة)
                </Typography>
                <Typography variant="h4">{statistics.creditNotes.total}</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  {statistics.creditNotes.totalAmount.toFixed(2)} ر.س
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ bgcolor: 'success.light' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  المقبولة
                </Typography>
                <Typography variant="h4">{statistics.creditNotes.accepted}</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  {statistics.creditNotes.acceptedAmount.toFixed(2)} ر.س
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ bgcolor: 'error.light' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  المرفوضة
                </Typography>
                <Typography variant="h4">{statistics.creditNotes.rejected}</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  {statistics.creditNotes.rejectedAmount.toFixed(2)} ر.س
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* الفلاتر */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="من تاريخ"
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="إلى تاريخ"
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              select
              label="الشهادة"
              value={filters.certificateId}
              onChange={(e) => setFilters({ ...filters, certificateId: e.target.value })}
            >
              <MenuItem value="">الكل</MenuItem>
              {certificates.map((cert) => (
                <MenuItem key={cert._id} value={cert._id}>
                  {cert.name} ({cert.environment})
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              select
              label="حالة الإرسال"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <MenuItem value="">الكل</MenuItem>
              <MenuItem value="pending">قيد الانتظار</MenuItem>
              <MenuItem value="submitted">تم الإرسال</MenuItem>
              <MenuItem value="accepted">مقبول</MenuItem>
              <MenuItem value="rejected">مرفوض</MenuItem>
              <MenuItem value="warning">تحذير</MenuItem>
              <MenuItem value="failed">فشل</MenuItem>
              <MenuItem value="validation_failed">فشل التحقق</MenuItem>
              <MenuItem value="error">خطأ</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="بحث"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="رقم الفاتورة أو العميل"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* أزرار الإجراءات الجماعية */}
      {selectedInvoices.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.light' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography>
              تم تحديد {selectedInvoices.length} فاتورة
            </Typography>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleBulkCreateCreditNotes}
              disabled={loading}
            >
              إرسال المحدد للهيئة
            </Button>
            <Button
              variant="outlined"
              onClick={() => setSelectedInvoices([])}
            >
              إلغاء التحديد
            </Button>
          </Stack>
        </Paper>
      )}

      {/* جدول الفواتير */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedInvoices.length === invoices.length && invoices.length > 0}
                  indeterminate={selectedInvoices.length > 0 && selectedInvoices.length < invoices.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>رقم الفاتورة</TableCell>
              <TableCell>التاريخ</TableCell>
              <TableCell>العميل</TableCell>
              <TableCell>المبلغ</TableCell>
              <TableCell>الشهادة</TableCell>
              <TableCell>حالة الإرسال</TableCell>
              <TableCell>فاتورة الاسترجاع</TableCell>
              <TableCell>الإجراءات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : invoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography color="textSecondary">لا توجد فواتير</Typography>
                </TableCell>
              </TableRow>
            ) : (
              invoices.map((invoice) => (
                <TableRow
                  key={invoice._id}
                  sx={{
                    bgcolor: invoice.hasSystemReturn ? 'warning.lighter' :
                            invoice.isB2B ? 'info.lighter' : 'inherit'
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedInvoices.includes(invoice._id)}
                      onChange={() => handleSelectInvoice(invoice._id)}
                      disabled={invoice.hasCreditNote || invoice.hasSystemReturn || invoice.isB2B}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography>{invoice.invoiceNumber}</Typography>
                      {invoice.hasSystemReturn && (
                        <Chip
                          size="small"
                          label={`مرتجع سابق (${invoice.systemReturnCount})`}
                          color="warning"
                          icon={<WarningIcon />}
                        />
                      )}
                      {invoice.isB2B && (
                        <Chip
                          size="small"
                          label="B2B"
                          color="info"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      // عرض التاريخ كما هو من قاعدة البيانات بدون تحويل
                      if (!invoice.date) return '-';
                      if (invoice.date.includes('T')) {
                        const datePart = invoice.date.split('T')[0];
                        const [year, month, day] = datePart.split('-');
                        return `${day}/${month}/${year}`;
                      }
                      return invoice.date;
                    })()}
                  </TableCell>
                  <TableCell>{invoice.customerName || '-'}</TableCell>
                  <TableCell>{invoice.total.toFixed(2)} ر.س</TableCell>
                  <TableCell>
                    {invoice.certificateName ? (
                      <Typography variant="body2">
                        {invoice.certificateName}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={getStatusText(invoice.zatcaPhase2?.submissionStatus)}
                      color={getStatusColor(invoice.zatcaPhase2?.submissionStatus)}
                    />
                  </TableCell>
                  <TableCell>
                    {invoice.hasCreditNote ? (
                      <Stack spacing={0.5}>
                        {invoice.creditNotes.map((cn) => (
                          <Box key={cn._id}>
                            <Chip
                              size="small"
                              icon={<ReceiptIcon />}
                              label={cn.creditNoteNumber}
                              color={getStatusColor(cn.submissionStatus)}
                              onClick={() => handleViewDetails(cn._id)}
                              sx={{ cursor: 'pointer' }}
                            />
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        لا يوجد
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {!invoice.hasCreditNote && !invoice.hasSystemReturn && !invoice.isB2B && (
                      <Tooltip title="إنشاء وإرسال فاتورة استرجاع">
                        <IconButton
                          color="primary"
                          onClick={() => handleCreateCreditNote(invoice._id)}
                          disabled={loading}
                        >
                          <SendIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {invoice.hasSystemReturn && (
                      <Tooltip title="لا يمكن إنشاء استرجاع - يوجد استرجاع سابق في النظام">
                        <span>
                          <IconButton disabled>
                            <SendIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {invoice.isB2B && !invoice.hasSystemReturn && (
                      <Tooltip title="لا يمكن إنشاء استرجاع - فاتورة B2B تحتاج Clearance">
                        <span>
                          <IconButton disabled>
                            <SendIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage="عدد الصفوف:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
        />
      </TableContainer>

      {/* Dialog تفاصيل فاتورة الاسترجاع */}
      <Dialog
        open={detailsDialog.open}
        onClose={() => setDetailsDialog({ open: false, creditNote: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>تفاصيل فاتورة الاسترجاع</DialogTitle>
        <DialogContent>
          {detailsDialog.creditNote && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    رقم فاتورة الاسترجاع
                  </Typography>
                  <Typography variant="body1">
                    {detailsDialog.creditNote.creditNoteNumber}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    التاريخ
                  </Typography>
                  <Typography variant="body1">
                    {(() => {
                      // عرض التاريخ والوقت كما هو من قاعدة البيانات بدون تحويل
                      const dateStr = detailsDialog.creditNote.creditNoteDate;
                      if (!dateStr) return '-';
                      if (dateStr.includes('T')) {
                        const [datePart, timePart] = dateStr.split('T');
                        const [year, month, day] = datePart.split('-');
                        const time = timePart.split('.')[0]; // إزالة الميلي ثانية
                        return `${day}/${month}/${year} ${time}`;
                      }
                      return dateStr;
                    })()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    المبلغ
                  </Typography>
                  <Typography variant="body1">
                    {detailsDialog.creditNote.creditNoteAmount.toFixed(2)} ر.س
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    الحالة
                  </Typography>
                  <Chip
                    label={getStatusText(detailsDialog.creditNote.submissionStatus)}
                    color={getStatusColor(detailsDialog.creditNote.submissionStatus)}
                  />
                </Grid>
                {detailsDialog.creditNote.certificateName && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      الشهادة المستخدمة
                    </Typography>
                    <Typography variant="body1">
                      {detailsDialog.creditNote.certificateName}
                    </Typography>
                  </Grid>
                )}
                
                {detailsDialog.creditNote.responseCode && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      رمز الاستجابة
                    </Typography>
                    <Chip
                      label={detailsDialog.creditNote.responseCode}
                      color={detailsDialog.creditNote.responseCode === '200' ? 'success' : 'error'}
                      size="small"
                    />
                  </Grid>
                )}

                {detailsDialog.creditNote.validationErrors && detailsDialog.creditNote.validationErrors.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                      الأخطاء والتحذيرات ({detailsDialog.creditNote.validationErrors.length})
                    </Typography>
                    <Stack spacing={1}>
                      {detailsDialog.creditNote.validationErrors.map((error, index) => {
                        console.log(`عرض خطأ/تحذير ${index + 1}:`, error);
                        return (
                        <Alert
                          key={index}
                          severity={error.severity === 'error' ? 'error' : error.severity === 'warning' ? 'warning' : 'info'}
                          sx={{ textAlign: 'right' }}
                        >
                          <Typography variant="body2">
                            {error.code && <strong>[{error.code}]</strong>} {error.message}
                            {error.field && <><br /><small>الحقل: {error.field}</small></>}
                          </Typography>
                        </Alert>
                      )})}
                    </Stack>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog({ open: false, creditNote: null })}>
            إغلاق
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ZatcaInvoiceReportPage;